import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { PageHeader } from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { useData } from "../lib/DataContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { PriorityBadge } from "../lib/ui";

const WL_COLOR = { "Low Load": "#16A34A", Normal: "#2563EB", High: "#CA8A04", Overloaded: "#DC2626" };

export default function Performance() {
  const [tab, setTab] = useState("attention");
  return (
    <div className="p-6">
      <PageHeader title="Performance & Control" subtitle="Attention, workload, aging, dependency impact and cross-department performance" />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap h-auto justify-start mb-4">
          {[["attention","Management Attention"],["workload","Workload & Capacity"],["aging","Aging"],["employees","Employees"],["departments","Departments"],["cross","Cross-Department"],["dependencies","Dependency Impact"],["executive","Executive Summary"],["weekly","Weekly Review"]].map(([k,l]) => (
            <TabsTrigger key={k} value={k} data-testid={`perf-tab-${k}`}>{l}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="attention"><Attention /></TabsContent>
        <TabsContent value="workload"><Workload /></TabsContent>
        <TabsContent value="aging"><Aging /></TabsContent>
        <TabsContent value="employees"><Employees /></TabsContent>
        <TabsContent value="departments"><Departments /></TabsContent>
        <TabsContent value="cross"><CrossDept /></TabsContent>
        <TabsContent value="dependencies"><DepImpact /></TabsContent>
        <TabsContent value="executive"><Executive /></TabsContent>
        <TabsContent value="weekly"><Weekly /></TabsContent>
      </Tabs>
    </div>
  );
}

function Attention() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const { userName, deptName } = useData();
  useEffect(() => { api.get("/management-attention").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  const bandColor = { Critical: "#DC2626", "High Attention": "#EA580C", "Medium Attention": "#CA8A04" };
  return (
    <div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        {Object.entries(data.counts).map(([k, v]) => (
          <div key={k} className="bg-white border rounded-xl p-4"><div className="text-2xl font-bold font-mono" style={{ color: bandColor[k] }}>{v}</div><div className="text-xs text-slate-500">{k}</div></div>
        ))}
      </div>
      <div className="space-y-2">
        {data.items.map((t) => (
          <button key={t.id} onClick={() => setOpenId(t.id)} data-testid={`attention-card-${t.human_id}`} className="w-full text-left bg-white border rounded-lg p-3 hover:bg-slate-50">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[10px] text-slate-400">{t.human_id}</span>
              <PriorityBadge value={t.priority} />
              <span className="text-xs font-semibold px-1.5 py-0.5 rounded text-white" style={{ backgroundColor: bandColor[t.band] }}>{t.band}</span>
              <span className="text-xs text-slate-400 font-mono">score {t.score}</span>
            </div>
            <div className="font-medium text-slate-800 mt-1">{t.title}</div>
            <div className="flex gap-1.5 mt-1 flex-wrap">{t.reasons.map((r) => <span key={r} className="text-[10px] bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{r}</span>)}</div>
            <div className="text-xs text-slate-500 mt-1">Action: {userName(t.current_action_owner_id)} · {deptName(t.department_id)}</div>
          </button>
        ))}
      </div>
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={() => api.get("/management-attention").then((r) => setData(r.data))} />}
    </div>
  );
}

function Workload() {
  const [rows, setRows] = useState([]);
  const { deptName } = useData();
  useEffect(() => { api.get("/workload-capacity").then((r) => setRows(r.data)); }, []);
  return (
    <Table cols={["Employee","Dept","Active","Critical","Due Week","Overdue","Est Hrs","Capacity","Load %","Level"]}>
      {rows.map((w) => (
        <tr key={w.id} className="border-b last:border-0" data-testid={`workload-${w.id}`}>
          <Td>{w.name}</Td><Td>{deptName(w.department_id)}</Td><Td>{w.active}</Td><Td className="text-red-600">{w.critical}</Td>
          <Td>{w.due_this_week}</Td><Td className="text-orange-600">{w.overdue}</Td><Td>{w.est_hours}</Td><Td>{w.capacity_hours}</Td>
          <Td className="font-mono">{w.workload_pct}%</Td>
          <Td><span className="text-xs rounded-full px-2 py-0.5" style={{ backgroundColor: (WL_COLOR[w.level]||"#64748B")+"20", color: WL_COLOR[w.level]||"#64748B" }}>{w.level}</span></Td>
        </tr>
      ))}
    </Table>
  );
}

function Aging() {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [active, setActive] = useState(null);
  useEffect(() => { api.get("/aging").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  const order = ["0-2 Days","3-7 Days","8-15 Days","16-30 Days","30+ Days"];
  const colors = ["#16A34A","#2563EB","#CA8A04","#EA580C","#DC2626"];
  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        {order.map((b, i) => (
          <button key={b} onClick={() => setActive(active === b ? null : b)} data-testid={`aging-bucket-${i}`} className="kpi-hover bg-white border rounded-xl p-4 text-left">
            <div className="text-2xl font-bold font-mono" style={{ color: colors[i] }}>{data.buckets[b].count}</div>
            <div className="text-xs text-slate-500">{b}</div>
          </button>
        ))}
      </div>
      {active && (
        <Table cols={["Task ID","Task","Age (d)","Overdue (d)","Priority","Status"]}>
          {data.buckets[active].tasks.map((t) => (
            <tr key={t.id} className="border-b last:border-0 hover:bg-slate-50 cursor-pointer" onClick={() => setOpenId(t.id)}>
              <Td className="font-mono text-xs">{t.human_id}</Td><Td>{t.title}</Td><Td>{t.age}</Td>
              <Td className="text-orange-600">{t.days_overdue}</Td><Td><PriorityBadge value={t.priority} /></Td><Td>{t.execution_status}</Td>
            </tr>
          ))}
        </Table>
      )}
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={() => {}} />}
    </div>
  );
}

function Employees() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/performance/employees").then((r) => setRows(r.data)); }, []);
  return (
    <Table cols={["Employee","Assigned","Completed","On-Time","Delayed","Active","Overdue","Blocked","On-Time %"]}>
      {rows.map((r) => (
        <tr key={r.id} className="border-b last:border-0"><Td>{r.name}</Td><Td>{r.assigned}</Td><Td className="text-emerald-600">{r.completed}</Td>
        <Td>{r.on_time}</Td><Td className="text-orange-600">{r.delayed}</Td><Td>{r.active}</Td><Td className="text-red-600">{r.overdue}</Td><Td>{r.blocked}</Td><Td className="font-mono">{r.on_time_pct}%</Td></tr>
      ))}
    </Table>
  );
}

function Departments() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/performance/departments").then((r) => setRows(r.data)); }, []);
  return (
    <Table cols={["Department","New","Completed","Closing Pending","Overdue","Blocked","On-Time %","Pending From Others","Pending w/ Dept"]}>
      {rows.map((r) => (
        <tr key={r.id} className="border-b last:border-0"><Td>{r.name}</Td><Td>{r.new_tasks}</Td><Td className="text-emerald-600">{r.completed}</Td>
        <Td>{r.closing_pending}</Td><Td className="text-orange-600">{r.overdue}</Td><Td>{r.blocked}</Td><Td className="font-mono">{r.on_time_pct}%</Td><Td>{r.pending_from_others}</Td><Td>{r.pending_with_dept}</Td></tr>
      ))}
    </Table>
  );
}

function CrossDept() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/performance/cross-department").then((r) => setRows(r.data)); }, []);
  return (
    <Table cols={["From","To","Requests","Pending","Completed","Overdue","SLA %"]}>
      {rows.map((r, i) => (
        <tr key={i} className="border-b last:border-0"><Td className="font-medium">{r.from}</Td><Td className="font-medium">→ {r.to}</Td><Td>{r.requests}</Td>
        <Td className="text-orange-600">{r.pending}</Td><Td className="text-emerald-600">{r.completed}</Td><Td className="text-red-600">{r.overdue}</Td><Td className="font-mono">{r.sla_pct}%</Td></tr>
      ))}
      {rows.length === 0 && <tr><Td colSpan={7} className="text-center py-8 text-slate-400">No cross-department handoffs.</Td></tr>}
    </Table>
  );
}

function DepImpact() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dependency-dashboard").then((r) => setData(r.data)); }, []);
  if (!data) return <Loading />;
  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-xl p-4">
        <div className="text-sm font-semibold mb-2">Top Blockers (tasks blocking the most downstream work)</div>
        {data.top_blockers.length === 0 ? <div className="text-sm text-slate-400">None</div> : data.top_blockers.map((b) => (
          <div key={b.human_id} className="text-sm flex items-center gap-2 border-b last:border-0 py-1.5"><span className="font-mono text-xs text-slate-400">{b.human_id}</span>{b.title}<span className="ml-auto text-xs bg-red-50 text-red-600 rounded-full px-2 py-0.5">blocks {b.blocks}</span></div>
        ))}
      </div>
      <Table cols={["Predecessor","","Successor","Type","Cross-Dept","Delay (d)"]}>
        {data.links.map((l) => (
          <tr key={l.id} className="border-b last:border-0"><Td><span className="font-mono text-xs text-slate-400">{l.predecessor.human_id}</span> {l.predecessor.title}</Td>
          <Td>→</Td><Td><span className="font-mono text-xs text-slate-400">{l.successor.human_id}</span> {l.successor.title}</Td>
          <Td>{l.dep_type}</Td><Td>{l.cross_department ? "Yes" : "—"}</Td><Td className={l.delay>0?"text-red-600":""}>{l.delay}</Td></tr>
        ))}
      </Table>
    </div>
  );
}

function Executive() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/executive-summary").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  const cards = [["open","Open"],["completed_today","Completed Today"],["due_today","Due Today"],["overdue","Overdue"],["critical_overdue","Critical Overdue"],["blocked","Blocked"],["pending_approval","Pending Approval"],["overdue_gt7","Overdue >7d"],["commitments_missed","Commitments Missed"]];
  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="text-sm font-semibold text-slate-700 mb-4">Task Matrix — Daily Execution Summary · {d.date}</div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
        {cards.map(([k,l]) => <div key={k}><div className="text-3xl font-bold font-mono text-slate-800">{d[k]}</div><div className="text-xs text-slate-500 mt-0.5">{l}</div></div>)}
      </div>
    </div>
  );
}

function Weekly() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/weekly-review").then((r) => setD(r.data)); }, []);
  if (!d) return <Loading />;
  const cards = [["closing_pending","Closing Pending"],["added","Tasks Added"],["closed","Tasks Closed"],["completion_pct","Completion %"],["on_time_pct","On-Time %"],["overdue","Overdue"],["blocked","Blocked"]];
  return (
    <div className="bg-white border rounded-xl p-6">
      <div className="text-sm font-semibold text-slate-700 mb-4">Weekly Management Review · week ending {d.week_ending}</div>
      <div className="grid grid-cols-3 md:grid-cols-5 gap-4">
        {cards.map(([k,l]) => <div key={k}><div className="text-3xl font-bold font-mono text-slate-800">{d[k]}{k.includes("pct")?"%":""}</div><div className="text-xs text-slate-500 mt-0.5">{l}</div></div>)}
      </div>
    </div>
  );
}

const Loading = () => <div className="text-slate-400 text-sm py-8">Loading…</div>;
function Table({ cols, children }) {
  return (
    <div className="bg-white border rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead><tr className="text-left border-b bg-slate-50 text-xs uppercase tracking-wide text-slate-500">{cols.map((c, i) => <th key={i} className="px-4 py-2.5">{c}</th>)}</tr></thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}
const Td = ({ children, className = "", colSpan }) => <td className={`px-4 py-2 ${className}`} colSpan={colSpan}>{children}</td>;
